import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { FaFileImage, FaSpinner } from "react-icons/fa";
import { MdClose } from "react-icons/md";

// Direct Cloudinary upload function
const uploadToCloudinary = async (file) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("upload_preset", "dev-wb");
  formData.append("cloud_name", "dqxlte6wf");

  try {
    const response = await fetch(
      `https://api.cloudinary.com/v1_1/dqxlte6wf/image/upload`,
      {
        method: "POST",
        body: formData,
      }
    );

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.statusText}`);
    }

    const data = await response.json();
    return data.secure_url;
  } catch (error) {
    console.error("Cloudinary upload error:", error);
    throw error;
  }
};

const AdminBlogModal = ({
  isOpen,
  onClose,
  onSubmit,
  initialData = {},
  isEditMode,
  isSubmitting,
}) => {
  // DEFAULT FORMS
  const defaultForm = { title: "", content: "", author: "", image: "" };

  // STATE
  const [formData, setFormData] = useState(defaultForm);
  const [imageData, setImageData] = useState(null);
  const [isImageUploading, setIsImageUploading] = useState(false);

  // PREFILL ON EDIT
  useEffect(() => {
    if (!isOpen) {
      // reset state when modal closes
      setFormData(defaultForm);
      setImageData(null);
      return;
    }

    if (!isEditMode || !initialData) {
      setFormData(defaultForm);
      setImageData(null);
      return;
    }

    setFormData({ ...defaultForm, ...initialData });

    // Format existing image if present
    if (initialData.image) {
      setImageData({
        id: "existing-image",
        url: initialData.image,
        preview: initialData.image,
        isUploading: false,
      });
    }
  }, [isOpen, initialData, isEditMode]);

  // PREVENT BACKGROUND SCROLL
  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  // HANDLE INPUT CHANGE
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // HANDLE IMAGE UPLOAD
  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      alert("File is too large. Maximum size is 10MB.");
      return;
    }

    // Create placeholder with unique ID
    const placeholder = {
      id: `temp-${Date.now()}-${Math.random()}`,
      file,
      preview: URL.createObjectURL(file),
      isUploading: true,
      url: null,
    };

    // Set placeholder immediately
    setImageData(placeholder);
    setIsImageUploading(true);

    try {
      const uploadedUrl = await uploadToCloudinary(file);

      if (uploadedUrl) {
        // Update with the uploaded URL
        setImageData((prev) => ({
          ...prev,
          url: uploadedUrl,
          isUploading: false,
        }));
      } else {
        // Remove failed upload
        setImageData(null);
      }
    } catch (error) {
      console.error("Upload failed for file:", file.name, error);
      // Remove failed upload
      setImageData(null);
    }

    setIsImageUploading(false);
    e.target.value = null; // clear file input
  };

  // REMOVE IMAGE
  const removeImage = () => {
    // Revoke object URL if it exists to prevent memory leaks
    if (imageData?.preview && imageData.preview.startsWith("blob:")) {
      URL.revokeObjectURL(imageData.preview);
    }

    setImageData(null);
  };

  // HANDLE FORM SUBMIT
  const handleSubmit = (e) => {
    e.preventDefault();

    // Check if image is still uploading
    if (isImageUploading) {
      alert("Please wait for the image to finish uploading before submitting.");
      return;
    }

    // Validation
    if (
      !formData.title.trim() ||
      !formData.content.trim() ||
      !formData.author.trim()
    ) {
      return;
    }

    const submitData = {
      ...formData,
      title: formData.title.trim(),
      content: formData.content.trim(),
      author: formData.author.trim(),
      image: imageData?.url || "",
    };

    onSubmit(submitData);
  };

  if (!isOpen) return null;

  // Render
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
    >
      <div className="bg-white w-full sm:max-w-2xl md:max-w-3xl lg:max-w-4xl px-6 pb-6 rounded-lg shadow-xl relative max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white z-20 py-4">
          <button
            type="button"
            onClick={onClose}
            aria-label="Close modal"
            className="absolute top-4 right-3 text-gray-700 flex items-center justify-center z-30"
          >
            <MdClose className="w-8 h-8" />
          </button>
          <h2 className="text-2xl font-bold mb-4">
            {isEditMode ? "Edit Blog" : "Add New Blog"}
          </h2>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Title */}
          <div>
            <label className="block font-medium mb-1">Blog Title</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className="w-full px-3 py-2 border-2 rounded outline-primaryColor/30"
              placeholder="Enter blog title"
              required
            />
          </div>

          {/* Author */}
          <div>
            <label className="block font-medium mb-1">Author</label>
            <input
              type="text"
              name="author"
              value={formData.author}
              onChange={handleChange}
              className="w-full px-3 py-2 border-2 rounded outline-primaryColor/30"
              placeholder="Enter author name"
              required
            />
          </div>

          {/* Blog Image */}
          <div>
            <label className="block font-medium mb-1">
              Blog Image{" "}
              <span className="text-xs text-amber-600">(Max size 10mb)</span>
            </label>
            <div className="flex items-start gap-4">
              <button
                type="button"
                onClick={() =>
                  document.getElementById("image-upload-blog")?.click()
                }
                className="flex flex-col items-center justify-center w-32 h-24 border-2 border-dashed border-gray-300 rounded-lg hover:border-primaryColor transition-colors"
                disabled={isImageUploading}
              >
                <FaFileImage className="text-2xl text-gray-400 mb-2" />
                <span className="text-sm text-gray-500">
                  {imageData ? "Change Image" : "Upload Image"}
                </span>
              </button>
              <input
                id="image-upload-blog"
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
                disabled={isImageUploading}
              />
              {imageData && (
                <div className="flex-1">
                  <div className="relative w-32 h-24">
                    <img
                      src={imageData.preview || imageData.url}
                      alt="Blog preview"
                      className="w-full h-full object-cover rounded border"
                    />
                    {imageData.isUploading && (
                      <div className="absolute inset-0 bg-black bg-opacity-40 flex justify-center items-center z-10">
                        <FaSpinner className="text-white animate-spin" />
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={removeImage}
                      className="absolute top-2 right-2 bg-red-600 text-white rounded-full w-5 h-5 pb-0.5 flex items-center justify-center shadow-md hover:bg-red-700 transition-colors"
                      disabled={imageData.isUploading}
                    >
                      ×
                    </button>
                  </div>
                </div>
              )}
            </div>
            {isImageUploading && (
              <p className="text-sm text-amber-600 mt-2">
                Uploading image... Please wait before submitting.
              </p>
            )}
          </div>

          {/* Content */}
          <div>
            <label className="block font-medium mb-1">Content</label>
            <textarea
              name="content"
              value={formData.content}
              onChange={handleChange}
              rows={8}
              className="w-full px-3 py-2 border-2 rounded outline-primaryColor/30 resize-y"
              placeholder="Write your blog content here..."
              required
            />
          </div>

          {/* Footer Buttons */}
          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded border border-gray-400 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || isImageUploading}
              className={`px-4 py-2 bg-green-600 ${
                isEditMode ? "w-40" : "w-20"
              } text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {isSubmitting ? (
                <FaSpinner className="text-white animate-spin mx-auto" />
              ) : isEditMode ? (
                "Save Changes"
              ) : (
                "Add"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default AdminBlogModal;
